// Dauber HQ — Feature gallery.
// Horizontal glass-tile shelf (Apple "Explore" pattern). A native scroll-snap row
// driven by timed dots and a play/pause control (swipe/scroll works too). It auto-advances:
// the active dot grows into a pill and fills like a timer over the dwell, then moves
// on. No scroll-jacking — vertical page scroll is never touched.

(function () {
  'use strict';

  var galleries = document.querySelectorAll('[data-feature-gallery]');
  if (!galleries.length) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var scrollBehavior = reduceMotion ? 'auto' : 'smooth';
  var DWELL = 5200; // ms each card holds before auto-advancing

  galleries.forEach(function (gallery) {
    var shelf = gallery.querySelector('[data-fg-shelf]');
    if (!shelf) return;

    var cards = Array.prototype.slice.call(shelf.querySelectorAll('[data-fg-card]'));
    if (!cards.length) return;
    var n = cards.length;

    var playBtn = gallery.querySelector('[data-fg-play]');
    var dotWrap = gallery.querySelector('[data-fg-dots]');

    // Build dots, each with a progress fill.
    var dots = [];
    if (dotWrap) {
      cards.forEach(function (card, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fg-dot';
        dot.setAttribute('aria-label', 'Show feature ' + (i + 1) + ' of ' + n);
        var fill = document.createElement('span');
        fill.className = 'fg-dot__fill';
        dot.appendChild(fill);
        dot.addEventListener('click', function () { goTo(i, true); });
        dotWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    // State. autoplay = the feature is enabled at all; the pause flags gate it.
    // `offscreen` holds autoplay until the gallery has fully scrolled into view.
    var autoplay = !reduceMotion && n > 1;
    var hoverPause = false, focusPause = false, hidden = false, userPaused = false, offscreen = true;
    var current = 0, targetLeft = 0, locked = false;
    var timer = null, lockTimer = null;

    function isPaused() { return hoverPause || focusPause || hidden || userPaused || offscreen; }
    function fillOf(i) { return dots[i] ? dots[i].querySelector('.fg-dot__fill') : null; }

    function stride() {
      return n < 2 ? cards[0].offsetWidth : cards[1].offsetLeft - cards[0].offsetLeft;
    }
    function cardLeft(i) { return cards[i].offsetLeft - shelf.offsetLeft; }
    function indexFromScroll() {
      var s = stride();
      if (s <= 0) return 0;
      return Math.max(0, Math.min(n - 1, Math.round(shelf.scrollLeft / s)));
    }

    // Restart the timer fill on the active dot.
    function paintFill(i) {
      var fill = fillOf(i);
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.width = '0%';
      void fill.offsetWidth; // reflow so the next transition starts from 0
      if (!autoplay) {
        // Reduced motion: the active dot reads as a solid mint pill, no timer.
        fill.style.width = '100%';
      } else if (!isPaused()) {
        fill.style.transition = 'width ' + DWELL + 'ms linear';
        fill.style.width = '100%';
      }
    }
    function freezeFill(i) {
      var fill = fillOf(i);
      if (!fill) return;
      var w = getComputedStyle(fill).width; // current animated px
      fill.style.transition = 'none';
      fill.style.width = w;
    }

    function setActive(i) {
      dots.forEach(function (d, k) {
        d.classList.toggle('fg-dot--active', k === i);
        d.setAttribute('aria-current', k === i ? 'true' : 'false');
        if (k !== i) {
          var f = d.querySelector('.fg-dot__fill');
          if (f) { f.style.transition = 'none'; f.style.width = '0%'; }
        }
      });
      paintFill(i);
    }

    function schedule() {
      clearTimeout(timer);
      if (!autoplay || isPaused()) return;
      timer = setTimeout(function () { goTo((current + 1) % n, false); }, DWELL);
    }

    // Move to a card. `manual` = a user gesture (dot/swipe); a click alone just
    // resets the dwell on the new card (autoplay only stops via hover/keyboard-focus).
    function goTo(i, manual) {
      i = ((i % n) + n) % n;
      current = i;
      targetLeft = cardLeft(i);
      locked = true;
      clearTimeout(lockTimer);
      lockTimer = setTimeout(function () { locked = false; }, 700);
      shelf.scrollTo({ left: targetLeft, behavior: scrollBehavior });
      setActive(i);
      schedule();
    }

    // Manual scroll / swipe → keep dots + paddles in sync, reset the dwell.
    var raf = null;
    shelf.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = null;
        if (locked && Math.abs(shelf.scrollLeft - targetLeft) < 6) locked = false;
        if (!locked) {
          var i = indexFromScroll();
          if (i !== current) { current = i; setActive(i); schedule(); }
        }
      });
    }, { passive: true });

    // Play / pause — a sticky user override, distinct from transient hover/focus pauses.
    function reflectPlayState() {
      if (!playBtn) return;
      playBtn.classList.toggle('fg-play--paused', userPaused);
      playBtn.setAttribute('aria-label', userPaused ? 'Play auto-advance' : 'Pause auto-advance');
    }
    if (playBtn) {
      if (!autoplay) {
        playBtn.style.display = 'none';
      } else {
        playBtn.addEventListener('click', function () {
          userPaused = !userPaused;
          reflectPlayState();
          if (isPaused()) { clearTimeout(timer); freezeFill(current); }
          else { paintFill(current); schedule(); }
        });
      }
    }

    // Transient pauses: hover, keyboard focus, hidden tab.
    function freeze() { clearTimeout(timer); freezeFill(current); }
    function thaw() { if (!isPaused()) { paintFill(current); schedule(); } }

    // Hover-pause is scoped to the CARDS, not the whole gallery. The gallery box
    // also contains the heading, the controls, and big top/bottom padding — binding
    // here meant moving off a card into any of that kept mouseenter/leave from
    // firing, so autoplay stayed paused until the pointer left the entire section.
    // mouseover/out (which bubble) + a closest('[data-fg-card]') check pause only
    // while genuinely over a card, and treat card→card moves as staying hovered.
    shelf.addEventListener('mouseover', function (e) {
      if (!hoverPause && e.target.closest('[data-fg-card]')) { hoverPause = true; freeze(); }
    });
    shelf.addEventListener('mouseout', function (e) {
      if (!hoverPause) return;
      var toCard = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('[data-fg-card]');
      if (!toCard) { hoverPause = false; thaw(); }
    });
    // Pause only for KEYBOARD focus (:focus-visible). A mouse click that focuses a
    // dot/play button must NOT pause-stick — pointer focus persists with no focusout,
    // so it would otherwise freeze autoplay until the window itself lost focus.
    gallery.addEventListener('focusin', function (e) {
      var keyboard = false;
      try { keyboard = !!(e.target && e.target.matches && e.target.matches(':focus-visible')); } catch (_) {}
      if (!keyboard) return;
      focusPause = true; freeze();
    });
    gallery.addEventListener('focusout', function () { focusPause = false; thaw(); });
    document.addEventListener('visibilitychange', function () {
      hidden = document.hidden;
      if (hidden) freeze(); else thaw();
    });

    // Honor the shelf's advertised arrow-key control (aria-label says so).
    shelf.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1, true); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1, true); }
    });

    window.addEventListener('resize', function () {
      // Re-anchor the active card after a layout change (stride/offsets shift).
      targetLeft = cardLeft(current);
      shelf.scrollTo({ left: targetLeft, behavior: 'auto' });
    }, { passive: true });

    // Hold autoplay until the cards shelf is fully in view, then release once.
    // Gate on the shelf (the cards row), not the whole gallery box — the box
    // includes the heading, controls, and tall padding and is usually taller
    // than the viewport, so it could never read as "fully visible".
    if (autoplay && 'IntersectionObserver' in window) {
      var vis = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          // Fully on screen, or (for a shelf taller than the viewport) scrolled
          // far enough that its bottom edge has come into view.
          var fully = e.intersectionRatio >= 0.99 ||
            (e.isIntersecting && e.boundingClientRect.bottom <= window.innerHeight + 1);
          if (offscreen && fully) {
            offscreen = false;
            vis.disconnect();
            thaw(); // paint the timer fill + schedule the first advance
          }
        });
      }, { threshold: [0, 0.25, 0.5, 0.75, 0.99, 1] });
      vis.observe(shelf);
    } else {
      offscreen = false;
    }

    // Init.
    reflectPlayState();
    setActive(0);
    schedule();
  });
})();
